import React from 'react';
import {
  Paper,
  Typography,
  Button,
  Box,
  useTheme,
  alpha,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import FeatureItem from './FeatureItem';

const PricingCard = ({ title, price, features, buttonText, isPopular, onClick, disabled, sx }) => {
  const theme = useTheme();
  
  return (
    <Paper
      elevation={8}
      sx={{
        p: { xs: 2.5, md: 3 },
        textAlign: "center",
        borderRadius: 3,
        position: "relative",
        overflow: "hidden",
        transition: "all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
        background: isPopular 
          ? `linear-gradient(135deg, ${alpha(theme.palette.primary.dark, 0.15)}, ${alpha(theme.palette.primary.main, 0.05)})` 
          : "rgba(255, 255, 255, 0.03)",
        backdropFilter: "blur(10px)",
        border: isPopular ? `2px solid ${alpha(theme.palette.primary.main, 0.5)}` : "1px solid rgba(255, 255, 255, 0.1)",
        "&:hover": {
          transform: "translateY(-8px)",
          boxShadow: isPopular ? `0 20px 40px ${alpha(theme.palette.primary.main, 0.3)}` : "0 20px 40px rgba(0, 0, 0, 0.4)",
        },
        ...sx,
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center' }}>
          {isPopular && (
            <Box
              sx={{
                background: "linear-gradient(45deg, #1976D2, #64B5F6)",
                color: "white",
                px: 2,
                py: 0.5,
                fontWeight: "bold",
                fontSize: "0.75rem",
                borderRadius: '20px',
                boxShadow: "0 2px 10px rgba(25, 118, 210, 0.3)",
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'linear-gradient(45deg, transparent, rgba(255,255,255,0.2), transparent)',
                  transform: 'translateX(-100%)',
                  transition: 'transform 0.6s ease',
                },
                '&:hover::before': {
                  transform: 'translateX(100%)',
                },
              }}
            >
              MOST POPULAR
            </Box>
          )}
          
          <Typography 
            variant="h5" 
            sx={{ 
              fontWeight: 700, 
              fontSize: { xs: '1.25rem', md: '1.5rem' },
              color: isPopular ? '#00E5FF' : 'text.primary',
              textShadow: isPopular ? `0 2px 4px ${alpha(theme.palette.primary.main, 0.2)}` : 'none',
              lineHeight: 1.3,
              textAlign: 'center',
            }}
          >
            {title}
          </Typography>
        </Box>
        
        {/* <Box sx={{ mb: 2 }}>
          <Typography
            variant="h3"
            color="primary"
            sx={{ 
              fontWeight: 800,
              fontSize: { xs: '2rem', md: '2.5rem' },
              display: "inline-flex",
              alignItems: "baseline",
              background: isPopular ? "linear-gradient(45deg, #1976D2, #64B5F6)" : "inherit",
              WebkitBackgroundClip: isPopular ? "text" : "inherit",
              WebkitTextFillColor: isPopular ? "transparent" : "inherit",
              textShadow: isPopular ? `0 2px 4px ${alpha(theme.palette.primary.main, 0.2)}` : 'none',
            }}
          >
            ${price}
            <Typography 
              component="span" 
              sx={{ 
                fontSize: "0.875rem", 
                ml: 0.5, 
                opacity: 0.8,
                color: isPopular ? 'inherit' : 'text.secondary',
              }}
            >
              /lead
            </Typography>
          </Typography>
        </Box> */}
        
        <Box 
          sx={{ 
            textAlign: "left", 
            mb: 3, 
            minHeight: { xs: 'auto', md: 'auto' },
            display: title === "Final Expense & ACA Live Transfer" ? 'grid' : 'flex',
            gridTemplateColumns: title === "Final Expense & ACA Live Transfer" ? { xs: '1fr', md: '1fr 1fr' } : '1fr',
            flexDirection: 'column',
            gap: title === "Final Expense & ACA Live Transfer" ? 0 : 2.5,
            flex: 1,
            '& .MuiBox-root': title === "Final Expense & ACA Live Transfer" ? {
              p: '0.25 !important',
              [theme.breakpoints.down('md')]: {
                p: '0.5rem !important',
              }
            } : {},
            ...(title === "Final Expense & ACA Live Transfer" && {
              [theme.breakpoints.down('md')]: {
                display: 'flex',
                gap: 0.75,
              }
            }),
          }}
        >
          {features.map((feature, index) => (
            <FeatureItem key={index}>{feature}</FeatureItem>
          ))}
        </Box>
        
        <Button
          variant={"contained"}
          color="primary"
          size="large"
          onClick={onClick}
          endIcon={<ArrowForwardIcon />}
          disabled={disabled}
          sx={{
            py: 1,
            px: 3,
            borderRadius: 2,
            fontWeight: 600,
            position: "relative",
            overflow: "hidden",
            transition: "all 0.3s ease",
            width: '100%',
            fontSize: { xs: '0.875rem', md: '1rem' },
            mt: 'auto',
            ...( {
              background: "linear-gradient(45deg, #1976D2, #64B5F6)",
              boxShadow: "0 4px 20px rgba(25, 118, 210, 0.4)",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: "-100%",
                width: "100%",
                height: "100%",
                background: "linear-gradient(45deg, transparent, rgba(255,255,255,0.3), transparent)",
                transition: "all 0.6s ease",
              },
              "&:hover": {
                boxShadow: "0 6px 25px rgba(25, 118, 210, 0.6)",
                "&::before": {
                  left: "100%",
                },
              },
              "&:disabled": {
                background: "linear-gradient(45deg, #1976D2, #64B5F6)",
                opacity: buttonText === "Processing..." ? 0.8 : 0.5,
              }
            }),
            ...(title === "Laser Targeted AI Sourced Lead" && {
              background: "linear-gradient(45deg, rgba(25, 118, 210, 0.6), rgba(100, 181, 246, 0.6))",
              color: "white",
              boxShadow: "0 4px 20px rgba(25, 118, 210, 0.2)",
              "&:hover": {
                background: "linear-gradient(45deg, rgba(25, 118, 210, 0.7), rgba(100, 181, 246, 0.7))",
                boxShadow: "0 6px 25px rgba(25, 118, 210, 0.3)",
              },
              "&:disabled": {
                background: "linear-gradient(45deg, rgba(25, 118, 210, 0.6), rgba(100, 181, 246, 0.6))",
                opacity: buttonText === "Processing..." ? 0.8 : 0.5,
                color: "white",
              }
            }),
            ...(title === "Done-For-You Pro" && {
              background: "linear-gradient(45deg, rgba(25, 118, 210, 0.6), rgba(100, 181, 246, 0.6))",
              color: "white",
              boxShadow: "0 4px 20px rgba(25, 118, 210, 0.2)",
              "&:hover": {
                background: "linear-gradient(45deg, rgba(25, 118, 210, 0.7), rgba(100, 181, 246, 0.7))",
                boxShadow: "0 6px 25px rgba(25, 118, 210, 0.3)",
              },
              "&:disabled": {
                background: "linear-gradient(45deg, rgba(25, 118, 210, 0.6), rgba(100, 181, 246, 0.6))",
                opacity: buttonText === "Processing..." ? 0.8 : 0.5,
                color: "white",
              }
            }),
           
          }}
        >
          {buttonText}
        </Button>
      </Box>
    </Paper>
  );
};

export default PricingCard; 